import React, { useEffect, useState } from "react";
import useAxios from "../../utils/useAxios";
import { 
  FaSpinner, 
  FaMapMarkerAlt, 
  FaCompass, 
  FaEye, 
  FaCalendarAlt, 
  FaSearch, 
  FaCity, 
  FaUser, 
  FaPhoneAlt, 
  FaStore, 
  FaFilter,
  FaUndo,
  FaShieldAlt
} from "react-icons/fa";

const customStyles = `
  .page-header-gradient {
    background: linear-gradient(135deg, #ffffff 0%, #fff7ed 100%);
    border-radius: 16px;
    padding: 24px;
    border: 1px solid #fed7aa;
    margin-bottom: 24px;
  }
  .stat-card {
    border: none;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(149, 157, 165, 0.08);
    transition: all 0.3s ease;
    overflow: hidden;
  }
  .stat-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(249, 115, 22, 0.12);
  }
  .stat-num-orange {
    background: linear-gradient(135deg, #ea580c, #c2410c);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 800;
    font-size: 2.3rem;
    line-height: 1;
  }
  .stat-num-blue {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 800;
    font-size: 2.3rem;
    line-height: 1;
  }
  .stat-num-green {
    background: linear-gradient(135deg, #16a34a, #15803d);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 800;
    font-size: 2.3rem;
    line-height: 1;
  }
  .stat-num-purple {
    background: linear-gradient(135deg, #9333ea, #7e22ce);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 800;
    font-size: 2.3rem;
    line-height: 1;
  }
  .table-card {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(149, 157, 165, 0.08);
    border: 1px solid #f1f5f9;
    overflow: hidden;
  }
  .btn-badge-nearme {
    background: #fff7ed;
    color: #ea580c;
    border: 1px solid #fed7aa;
  }
  .btn-badge-nearcity {
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #bfdbfe;
  }
  .btn-badge-details-res {
    background: #f0fdf4;
    color: #16a34a;
    border: 1px solid #bbf7d0;
  }
  .btn-badge-details-city {
    background: #faf5ff;
    color: #9333ea;
    border: 1px solid #e9d5ff;
  }
`;

const RestaurantActionClicks = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    summary: {
      total_clicks: 0,
      near_me_clicks: 0,
      view_details_restaurants_clicks: 0,
      nearest_city_clicks: 0,
      view_details_city_clicks: 0,
    },
    cities: [],
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 1,
    data: [],
  });

  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedButton, setSelectedButton] = useState("all");
  const [selectedCity, setSelectedCity] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // 4 Independent Button Login Requirement Flags
  const [flags, setFlags] = useState({
    require_login_near_me: true,
    require_login_city_nearest: true,
    require_login_restaurants_details: true,
    require_login_city_details: true,
  });
  const [updatingFlagKey, setUpdatingFlagKey] = useState(null);

  const axiosAPI = useAxios();

  const fetchFlags = async () => {
    try {
      const res = await axiosAPI.get("/api/settings/feature-flags");
      if (res?.data?.success && res.data.flags) {
        setFlags({
          require_login_near_me: !!res.data.flags.require_login_near_me,
          require_login_city_nearest: !!res.data.flags.require_login_city_nearest,
          require_login_restaurants_details: !!res.data.flags.require_login_restaurants_details,
          require_login_city_details: !!res.data.flags.require_login_city_details,
        });
      }
    } catch (err) {
      console.error("Error loading button login flags:", err);
    }
  };

  useEffect(() => {
    fetchFlags();
  }, []);

  const handleToggleFlag = async (key, label) => {
    try {
      setUpdatingFlagKey(key);
      const newVal = !flags[key];
      const payload = { [key]: newVal };

      const res = await axiosAPI.put("/api/settings/feature-flags", payload);
      if (res?.data?.success) {
        setFlags((prev) => ({ ...prev, [key]: newVal }));
        if (typeof window.notifyMessage === "function") {
          window.notifyMessage(
            "Updated",
            `${label} login requirement turned ${newVal ? "ON" : "OFF"}.`,
            "success"
          );
        }
      }
    } catch (err) {
      console.error("Error toggling flag:", err);
      if (typeof window.notifyMessage === "function") {
        window.notifyMessage("Error", "Failed to update requirement.", "danger");
      }
    } finally {
      setUpdatingFlagKey(null);
    }
  };

  const fetchActionClicks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (selectedButton && selectedButton !== "all") params.append("button_key", selectedButton);
      if (selectedCity) params.append("city", selectedCity);
      if (search) params.append("search", search);
      params.append("page", page);
      params.append("limit", 50);

      const res = await axiosAPI.get(`/api/restaurant-actions/stats?${params.toString()}`);
      if (res?.data?.success) {
        setData(res.data);
      }
    } catch (error) {
      console.error("Error fetching restaurant action clicks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActionClicks();
  }, [page, selectedButton, selectedCity]);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setPage(1);
    fetchActionClicks();
  };

  const handleResetFilters = () => {
    setStartDate("");
    setEndDate("");
    setSelectedButton("all");
    setSelectedCity("");
    setSearch("");
    setPage(1);
  };

  const getButtonBadge = (key, name) => {
    switch (key) {
      case "near_me_restaurants":
        return (
          <span className="badge btn-badge-nearme px-2.5 py-1.5 rounded-pill d-inline-flex align-items-center gap-1.5 fw-bold">
            <FaCompass /> {name || "Near Me (Restaurants Page)"}
          </span>
        );
      case "nearest_city":
        return (
          <span className="badge btn-badge-nearcity px-2.5 py-1.5 rounded-pill d-inline-flex align-items-center gap-1.5 fw-bold">
            <FaMapMarkerAlt /> {name || "Find Nearest (City Page)"}
          </span>
        );
      case "view_details_restaurants":
        return (
          <span className="badge btn-badge-details-res px-2.5 py-1.5 rounded-pill d-inline-flex align-items-center gap-1.5 fw-bold">
            <FaEye /> {name || "View Details (Restaurants Page)"}
          </span>
        );
      case "view_details_city":
        return (
          <span className="badge btn-badge-details-city px-2.5 py-1.5 rounded-pill d-inline-flex align-items-center gap-1.5 fw-bold">
            <FaStore /> {name || "View Details (City Page)"}
          </span>
        );
      default:
        return <span className="badge bg-secondary">{name || key}</span>;
    }
  };

  const summary = data?.summary || {
    total_clicks: 0,
    near_me_clicks: 0,
    view_details_restaurants_clicks: 0,
    nearest_city_clicks: 0,
    view_details_city_clicks: 0,
  };

  return (
    <>
      <style>{customStyles}</style>
      <div className="container">
        <div className="page-inner">
          {/* Header */}
          <div className="page-header-gradient d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="badge bg-orange text-white" style={{ background: "#ea580c" }}>
                  BUTTON INTERACTION ANALYTICS
                </span>
                <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                  All 4 Restaurant Action Buttons
                </span>
              </div>
              <h3 className="fw-bold text-dark mb-1">Restaurant Button Actions & Clicks</h3>
              <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                Real-time tracking of Near Me & View Details clicks with user information, restaurant names, and location data.
              </p>
            </div>
            <button
              onClick={() => {
                fetchActionClicks();
                fetchFlags();
              }}
              disabled={loading}
              className="btn btn-dark btn-round d-flex align-items-center gap-2"
            >
              {loading ? <FaSpinner className="spinner" /> : <FaUndo />}
              <span>Refresh Data</span>
            </button>
          </div>

          {/* Access & Login Requirements - 4 Independent Toggles */}
          <div
            className="card shadow-sm border-0 mb-4"
            style={{
              borderRadius: "16px",
              background: "linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)",
              borderLeft: "5px solid #ea580c",
            }}
          >
            <div className="card-body p-3 p-md-4">
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3 pb-2 border-bottom border-warning-subtle">
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="badge bg-warning text-dark font-weight-bold px-2.5 py-1.5 rounded-pill d-inline-flex align-items-center gap-1"
                    style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
                  >
                    <FaShieldAlt /> ACCESS CONTROL
                  </span>
                  <h5 className="mb-0 fw-bold text-dark">
                    Login Requirements for All 4 Buttons
                  </h5>
                </div>
                <small className="text-muted">
                  Turn login requirement ON or OFF independently for each of the 4 buttons.
                </small>
              </div>

              <div className="row g-3">
                {/* Button 1 Toggle: Near Me (Restaurants Page) */}
                <div className="col-12 col-md-6 col-xl-3">
                  <div
                    className="p-3 bg-white rounded-3 border d-flex flex-column justify-content-between shadow-sm h-100"
                    style={{ borderColor: "#fed7aa" }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "8px",
                            background: "#fff7ed",
                            color: "#ea580c",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1rem",
                          }}
                        >
                          <FaCompass />
                        </div>
                        <div>
                          <div className="fw-bold text-dark" style={{ fontSize: "0.88rem" }}>
                            Near Me
                          </div>
                          <small className="text-muted d-block" style={{ fontSize: "0.72rem" }}>
                            Restaurants Page (/restaurants)
                          </small>
                        </div>
                      </div>
                      <span
                        className={`badge ${flags.require_login_near_me ? "bg-success" : "bg-secondary"}`}
                        style={{ fontSize: "0.75rem", borderRadius: "12px" }}
                      >
                        {flags.require_login_near_me ? "ON" : "OFF"}
                      </span>
                    </div>
                    <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                      <span className="text-muted" style={{ fontSize: "0.78rem" }}>
                        Require Login:
                      </span>
                      <div className="form-check form-switch m-0" style={{ fontSize: "1.25rem" }}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="switch_near_me"
                          checked={flags.require_login_near_me}
                          disabled={updatingFlagKey === "require_login_near_me"}
                          onChange={() => handleToggleFlag("require_login_near_me", "Near Me (/restaurants)")}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Button 2 Toggle: Find Nearest (City Page) */}
                <div className="col-12 col-md-6 col-xl-3">
                  <div
                    className="p-3 bg-white rounded-3 border d-flex flex-column justify-content-between shadow-sm h-100"
                    style={{ borderColor: "#bfdbfe" }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "8px",
                            background: "#eff6ff",
                            color: "#2563eb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1rem",
                          }}
                        >
                          <FaMapMarkerAlt />
                        </div>
                        <div>
                          <div className="fw-bold text-dark" style={{ fontSize: "0.88rem" }}>
                            Find Nearest
                          </div>
                          <small className="text-muted d-block" style={{ fontSize: "0.72rem" }}>
                            City Page (/restaurants/:city)
                          </small>
                        </div>
                      </div>
                      <span
                        className={`badge ${flags.require_login_city_nearest ? "bg-success" : "bg-secondary"}`}
                        style={{ fontSize: "0.75rem", borderRadius: "12px" }}
                      >
                        {flags.require_login_city_nearest ? "ON" : "OFF"}
                      </span>
                    </div>
                    <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                      <span className="text-muted" style={{ fontSize: "0.78rem" }}>
                        Require Login:
                      </span>
                      <div className="form-check form-switch m-0" style={{ fontSize: "1.25rem" }}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="switch_city_nearest"
                          checked={flags.require_login_city_nearest}
                          disabled={updatingFlagKey === "require_login_city_nearest"}
                          onChange={() => handleToggleFlag("require_login_city_nearest", "Find Nearest (City Page)")}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Button 3 Toggle: View Details (Restaurants Page) */}
                <div className="col-12 col-md-6 col-xl-3">
                  <div
                    className="p-3 bg-white rounded-3 border d-flex flex-column justify-content-between shadow-sm h-100"
                    style={{ borderColor: "#bbf7d0" }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "8px",
                            background: "#f0fdf4",
                            color: "#16a34a",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1rem",
                          }}
                        >
                          <FaEye />
                        </div>
                        <div>
                          <div className="fw-bold text-dark" style={{ fontSize: "0.88rem" }}>
                            View Details
                          </div>
                          <small className="text-muted d-block" style={{ fontSize: "0.72rem" }}>
                            Restaurants Page (/restaurants)
                          </small>
                        </div>
                      </div>
                      <span
                        className={`badge ${flags.require_login_restaurants_details ? "bg-success" : "bg-secondary"}`}
                        style={{ fontSize: "0.75rem", borderRadius: "12px" }}
                      >
                        {flags.require_login_restaurants_details ? "ON" : "OFF"}
                      </span>
                    </div>
                    <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                      <span className="text-muted" style={{ fontSize: "0.78rem" }}>
                        Require Login:
                      </span>
                      <div className="form-check form-switch m-0" style={{ fontSize: "1.25rem" }}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="switch_restaurants_details"
                          checked={flags.require_login_restaurants_details}
                          disabled={updatingFlagKey === "require_login_restaurants_details"}
                          onChange={() => handleToggleFlag("require_login_restaurants_details", "View Details (/restaurants)")}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Button 4 Toggle: View Details (City Page) */}
                <div className="col-12 col-md-6 col-xl-3">
                  <div
                    className="p-3 bg-white rounded-3 border d-flex flex-column justify-content-between shadow-sm h-100"
                    style={{ borderColor: "#e9d5ff" }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "8px",
                            background: "#faf5ff",
                            color: "#9333ea",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1rem",
                          }}
                        >
                          <FaStore />
                        </div>
                        <div>
                          <div className="fw-bold text-dark" style={{ fontSize: "0.88rem" }}>
                            View Details
                          </div>
                          <small className="text-muted d-block" style={{ fontSize: "0.72rem" }}>
                            City Page (/restaurants/:city)
                          </small>
                        </div>
                      </div>
                      <span
                        className={`badge ${flags.require_login_city_details ? "bg-success" : "bg-secondary"}`}
                        style={{ fontSize: "0.75rem", borderRadius: "12px" }}
                      >
                        {flags.require_login_city_details ? "ON" : "OFF"}
                      </span>
                    </div>
                    <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                      <span className="text-muted" style={{ fontSize: "0.78rem" }}>
                        Require Login:
                      </span>
                      <div className="form-check form-switch m-0" style={{ fontSize: "1.25rem" }}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="switch_city_details"
                          checked={flags.require_login_city_details}
                          disabled={updatingFlagKey === "require_login_city_details"}
                          onChange={() => handleToggleFlag("require_login_city_details", "View Details (City Page)")}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 4 Summary Stat Cards */}
          <div className="row g-3 mb-4">
            {/* Card 1: Near Me (Restaurants Page) */}
            <div className="col-12 col-sm-6 col-xl-3">
              <div className="stat-card p-4 h-100 border-start border-4 border-warning">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted fw-bold" style={{ fontSize: "0.8rem", textTransform: "uppercase" }}>
                    Button 1: Near Me
                  </span>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      background: "#fff7ed",
                      color: "#ea580c",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaCompass />
                  </div>
                </div>
                <div className="stat-num-orange">{summary.near_me_clicks || 0}</div>
                <small className="text-muted">Restaurants Page Near Me clicks</small>
              </div>
            </div>

            {/* Card 2: Find Nearest (City Page) */}
            <div className="col-12 col-sm-6 col-xl-3">
              <div className="stat-card p-4 h-100 border-start border-4 border-primary">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted fw-bold" style={{ fontSize: "0.8rem", textTransform: "uppercase" }}>
                    Button 2: Find Nearest
                  </span>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      background: "#eff6ff",
                      color: "#2563eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaMapMarkerAlt />
                  </div>
                </div>
                <div className="stat-num-blue">{summary.nearest_city_clicks || 0}</div>
                <small className="text-muted">City Page 15 km filter clicks</small>
              </div>
            </div>

            {/* Card 3: View Details (Restaurants Page) */}
            <div className="col-12 col-sm-6 col-xl-3">
              <div className="stat-card p-4 h-100 border-start border-4 border-success">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted fw-bold" style={{ fontSize: "0.8rem", textTransform: "uppercase" }}>
                    Button 3: View Details
                  </span>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      background: "#f0fdf4",
                      color: "#16a34a",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaEye />
                  </div>
                </div>
                <div className="stat-num-green">{summary.view_details_restaurants_clicks || 0}</div>
                <small className="text-muted">Restaurants Page card details</small>
              </div>
            </div>

            {/* Card 4: View Details (City Page) */}
            <div className="col-12 col-sm-6 col-xl-3">
              <div className="stat-card p-4 h-100 border-start border-4 border-purple" style={{ borderLeftColor: "#9333ea !important" }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted fw-bold" style={{ fontSize: "0.8rem", textTransform: "uppercase" }}>
                    Button 4: View Details
                  </span>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      background: "#faf5ff",
                      color: "#9333ea",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaStore />
                  </div>
                </div>
                <div className="stat-num-purple">{summary.view_details_city_clicks || 0}</div>
                <small className="text-muted">City Page card details</small>
              </div>
            </div>
          </div>

          {/* Filters Card */}
          <div className="card shadow-sm border-0 mb-4 rounded-3">
            <div className="card-body p-3 p-md-4">
              <form onSubmit={handleApplyFilters} className="row g-3 align-items-end">
                {/* Button Filter */}
                <div className="col-12 col-md-3">
                  <label className="form-label fw-bold text-dark small mb-1">
                    <FaFilter className="me-1 text-primary" /> Filter by Button
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedButton}
                    onChange={(e) => {
                      setSelectedButton(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="all">All 4 Buttons</option>
                    <option value="near_me_restaurants">Near Me (Restaurants Page)</option>
                    <option value="nearest_city">Find Nearest (City Page)</option>
                    <option value="view_details_restaurants">View Details (Restaurants Page)</option>
                    <option value="view_details_city">View Details (City Page)</option>
                  </select>
                </div>

                {/* City Filter */}
                <div className="col-12 col-md-2">
                  <label className="form-label fw-bold text-dark small mb-1">
                    <FaCity className="me-1 text-primary" /> City
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedCity}
                    onChange={(e) => {
                      setSelectedCity(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Cities</option>
                    {data.cities?.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Start Date */}
                <div className="col-12 col-md-2">
                  <label className="form-label fw-bold text-dark small mb-1">
                    <FaCalendarAlt className="me-1 text-primary" /> From Date
                  </label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                {/* End Date */}
                <div className="col-12 col-md-2">
                  <label className="form-label fw-bold text-dark small mb-1">
                    <FaCalendarAlt className="me-1 text-primary" /> To Date
                  </label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                {/* Search Text */}
                <div className="col-12 col-md-3">
                  <label className="form-label fw-bold text-dark small mb-1">
                    <FaSearch className="me-1 text-primary" /> Search User / Restaurant
                  </label>
                  <div className="input-group input-group-sm">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Name, phone, restaurant..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <button type="submit" className="btn btn-dark">
                      Search
                    </button>
                    {(startDate || endDate || selectedButton !== "all" || selectedCity || search) && (
                      <button
                        type="button"
                        onClick={handleResetFilters}
                        className="btn btn-outline-secondary"
                        title="Reset Filters"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Action Clicks Table */}
          <div className="table-card">
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h5 className="fw-bold mb-0 text-dark">
                  Recent Button Clicks ({data.total || 0})
                </h5>
                <small className="text-muted">
                  Showing page {data.page} of {data.totalPages || 1}
                </small>
              </div>
              <span className="badge bg-light text-dark border">
                Total Tracked: {summary.total_clicks || 0}
              </span>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr style={{ fontSize: "0.82rem", textTransform: "uppercase" }}>
                    <th className="py-3 px-3">Sr No</th>
                    <th className="py-3">Button Action</th>
                    <th className="py-3">User Details</th>
                    <th className="py-3">Target Restaurant</th>
                    <th className="py-3">City / Page</th>
                    <th className="py-3">Location Coordinates</th>
                    <th className="py-3 px-3">Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5">
                        <FaSpinner className="spinner me-2" /> Loading action clicks...
                      </td>
                    </tr>
                  ) : !data.data || data.data.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5 text-muted">
                        No button clicks recorded yet for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    data.data.map((row, idx) => (
                      <tr key={row.id}>
                        {/* Index */}
                        <td className="px-3 fw-bold text-muted" style={{ fontSize: "0.85rem" }}>
                          {(data.page - 1) * data.limit + idx + 1}
                        </td>

                        {/* Button Action */}
                        <td>{getButtonBadge(row.button_key, row.button_name)}</td>

                        {/* User Details */}
                        <td>
                          {row.user_id ? (
                            <div>
                              <div className="fw-bold text-dark d-flex align-items-center gap-1.5" style={{ fontSize: "0.9rem" }}>
                                <FaUser className="text-primary" style={{ fontSize: "0.75rem" }} />
                                {row.user_name || `User #${row.user_id}`}
                              </div>
                              {row.user_phone && (
                                <small className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "0.8rem" }}>
                                  <FaPhoneAlt style={{ fontSize: "0.65rem" }} />
                                  {row.user_phone}
                                </small>
                              )}
                              {row.user_email && (
                                <small className="text-muted d-block" style={{ fontSize: "0.75rem" }}>
                                  {row.user_email}
                                </small>
                              )}
                            </div>
                          ) : (
                            <span className="badge bg-light text-muted border" style={{ fontSize: "0.75rem" }}>
                              Guest Visitor
                            </span>
                          )}
                        </td>

                        {/* Target Restaurant */}
                        <td>
                          {row.restaurant_name ? (
                            <div className="fw-bold text-dark" style={{ fontSize: "0.88rem" }}>
                              <FaStore className="text-muted me-1" />
                              {row.restaurant_name}
                            </div>
                          ) : row.restaurant_id ? (
                            <span className="text-muted small">Restaurant #{row.restaurant_id}</span>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>

                        {/* City / Page URL */}
                        <td>
                          {row.city_name && (
                            <div className="fw-semibold text-dark small">
                              📍 {row.city_name}
                            </div>
                          )}
                          {row.page_url && (
                            <code style={{ fontSize: "0.75rem", background: "#f8fafc", padding: "2px 6px", borderRadius: "4px" }}>
                              {row.page_url}
                            </code>
                          )}
                        </td>

                        {/* Coordinates */}
                        <td>
                          {row.latitude && row.longitude ? (
                            <small className="text-muted font-monospace" style={{ fontSize: "0.78rem" }}>
                              {parseFloat(row.latitude).toFixed(4)}, {parseFloat(row.longitude).toFixed(4)}
                            </small>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>

                        {/* Date & Time */}
                        <td className="px-3" style={{ fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                          <span className="text-dark fw-semibold">
                            {row.created_at ? row.created_at.split(" ")[0] : "—"}
                          </span>
                          <span className="text-muted d-block small">
                            {row.created_at ? row.created_at.split(" ")[1] : ""}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {data.totalPages > 1 && (
              <div className="p-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                <small className="text-muted">
                  Showing {(data.page - 1) * data.limit + 1} to{" "}
                  {Math.min(data.page * data.limit, data.total)} of {data.total} entries
                </small>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-dark"
                    disabled={data.page <= 1}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-sm btn-outline-dark"
                    disabled={data.page >= data.totalPages}
                    onClick={() => setPage((prev) => prev + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RestaurantActionClicks;
